import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback } from "react";
import { buildApiTokenRequest } from "../auth/msalConfig";
import { useTenant } from "../auth/TenantContext";
import { trackException } from "../telemetry";
import type {
    ProjectListResponse,
    ProjectResponse,
    SessionResponse,
    SessionListResponse,
    ActionLogEntry,
} from "./models/ApiModels";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const useLocalTokenBridge = import.meta.env.VITE_USE_LOCAL_TOKEN_BRIDGE === "true";
const localTokenBridgeUrl = import.meta.env.VITE_LOCAL_TOKEN_BRIDGE_URL || "http://localhost:3031/token";

function buildUrl(path: string, resourceId: string): string {
    const cleanPath = path.startsWith("/") ? path.substring(1) : path;
    return `${BASE_URL}/${cleanPath}?resourceId=${encodeURIComponent(resourceId)}`;
}

async function fetchWithToken<T>(url: string, token: string, method = "GET", body?: unknown): Promise<T> {
    const response = await fetch(url, {
        method,
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
        const err = new Error(`HTTP ${response.status}: ${await response.text()}`);
        trackException(err, { source: "api-client", method, url: new URL(url).pathname });
        throw err;
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
}

/**
 * React hook that returns an API client bound to the current MSAL account.
 * Each method acquires a fresh token silently (falls back to popup on failure).
 */
export function useTestingAgentClient() {
    const { instance, accounts } = useMsal();
    const { selectedTenantId } = useTenant();

    const acquireToken = useCallback(async (): Promise<string> => {
        if (useLocalTokenBridge) {
            const response = await fetch(localTokenBridgeUrl);
            if (!response.ok) {
                throw new Error(`Token bridge failed: ${await response.text()}`);
            }
            const body = (await response.json()) as { accessToken?: string };
            if (!body.accessToken) {
                throw new Error("Token bridge returned no access token");
            }
            return body.accessToken;
        }

        const account = instance.getActiveAccount() ?? accounts[0];
        if (!account) throw new Error("No authenticated account");
        if (!selectedTenantId) {
            throw new Error("No tenant selected");
        }

        const request = { ...buildApiTokenRequest(selectedTenantId), account };

        try {
            const r = await instance.acquireTokenSilent(request);
            if (r.account) {
                instance.setActiveAccount(r.account);
            }
            return r.accessToken;
        } catch (error) {
            if (!(error instanceof InteractionRequiredAuthError)) {
                throw error;
            }

            // Session expired or first-time access to this tenant — use popup
            // so the page stays alive (redirect would wipe TenantContext state).
            const r2 = await instance.acquireTokenPopup(request);
            if (r2.account) instance.setActiveAccount(r2.account);
            return r2.accessToken;
        }
    }, [instance, accounts, selectedTenantId]);

    const getProjects = useCallback(
        async (resourceId: string, pageSize?: number, continuationToken?: string): Promise<ProjectListResponse> => {
            const token = await acquireToken();
            let url = buildUrl("projects", resourceId);
            const params: string[] = [];
            if (pageSize) params.push(`pageSize=${pageSize}`);
            if (continuationToken) params.push(`continuationToken=${encodeURIComponent(continuationToken)}`);
            if (params.length) url += `&${params.join("&")}`;
            return fetchWithToken<ProjectListResponse>(url, token);
        },
        [acquireToken],
    );

    const getProject = useCallback(
        async (resourceId: string, projectId: string): Promise<ProjectResponse> => {
            const token = await acquireToken();
            const url = buildUrl(`projects/${projectId}`, resourceId);
            return fetchWithToken<ProjectResponse>(url, token);
        },
        [acquireToken],
    );

    const getSessions = useCallback(
        async (
            resourceId: string,
            projectId: string,
            options?: {
                top?: number;
                createdFrom?: string;
                createdTo?: string;
                continuationToken?: string;
                commandTypes?: string[]; // e.g. ["run", "scout"]
                hasTestResults?: boolean;
                hasActions?: boolean;
                /** Case-insensitive substring matched against each session's
                 * action log entries (title/description/intent). */
                search?: string;
                /** Filter action logs to these types (e.g. ["scout", "heal"]). */
                actionTypes?: string[];
            },
        ): Promise<SessionListResponse> => {
            const token = await acquireToken();
            let url = buildUrl(`projects/${projectId}/sessions`, resourceId);
            const params: string[] = ["commandTypes=" + (options?.commandTypes?.join(",") ?? "")];
            if (options?.hasTestResults != null){
                params.push(`hasTestResults=${options.hasTestResults}`);
            }
            if (options?.hasActions != null){
                params.push(`hasActions=${options.hasActions}`);
            }
            if (options?.search) params.push(`search=${encodeURIComponent(options.search)}`);
            if (options?.actionTypes?.length) params.push(`actions=${options.actionTypes.join(",")}`);
            if (options?.top) params.push(`top=${options.top}`);
            if (options?.createdFrom) params.push(`createdFrom=${encodeURIComponent(options.createdFrom)}`);
            if (options?.createdTo) params.push(`createdTo=${encodeURIComponent(options.createdTo)}`);
            if (options?.continuationToken) params.push(`continuationToken=${encodeURIComponent(options.continuationToken)}`);
            url += `&${params.join("&")}`;
            return fetchWithToken<SessionListResponse>(url, token);
        },
        [acquireToken],
    );

    // Outdated
    const getActionLogs = useCallback(
        async (resourceId: string, projectId: string): Promise<ActionLogEntry[]> => {
            const token = await acquireToken();
            const url = buildUrl(`projects/${encodeURIComponent(projectId)}/action-logs`, resourceId);
            return fetchWithToken<ActionLogEntry[]>(url, token);
        },
        [acquireToken],
    );

    const getSession = useCallback(
        async (resourceId: string, projectId: string, sessionId: string): Promise<SessionResponse> => {
            const token = await acquireToken();
            const url = buildUrl(`projects/${projectId}/sessions/${sessionId}`, resourceId);
            return fetchWithToken<SessionResponse>(url, token);
        },
        [acquireToken],
    );

    return { getProjects, getProject, getSessions, getSession, getActionLogs };
}
